import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Camera,
  Mountain,
  Calendar,
  Video,
  Image,
  MapPin,
  Clock,
  Users,
} from 'lucide-react';

const Services: React.FC = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: <Camera size={48} className="text-primary-orange" />,
      title: 'Şehir Çekimleri',
      description:
        "Ankara'nın en güzel açılarından şehir manzaralarını drone ile çekiyoruz.",
      features: [
        'Yüksek çözünürlüklü fotoğraf çekimi',
        '4K video kaydı',
        'Farklı açılardan çekim',
        'Profesyonel düzenleme',
        'Hızlı teslimat',
      ],
      price: '₺2,500',
      duration: '1-2 gün',
      serviceKey: 'urban',
    },
    {
      icon: <Mountain size={48} className="text-primary-orange" />,
      title: 'Doğa & Manzara',
      description: 'Doğanın muhteşem güzelliklerini havadan görüntülüyoruz.',
      features: [
        'Geniş açı manzara çekimi',
        'Doğal ışık optimizasyonu',
        'HDR fotoğraf teknikleri',
        'Panoramik görüntüler',
        'Çevre dostu yaklaşım',
      ],
      price: '₺3,000',
      duration: '2-3 gün',
      serviceKey: 'nature',
    },
    {
      icon: <Calendar size={48} className="text-primary-orange" />,
      title: 'Etkinlikler & Özel Günler',
      description: 'Düğün, nişan ve özel etkinliklerinizi unutulmaz kılıyoruz.',
      features: [
        'Özel gün çekimleri',
        'Düğün videoları',
        'Nişan törenleri',
        'Kurumsal etkinlikler',
        'Özel efektler',
      ],
      price: '₺4,500',
      duration: '1 gün',
      serviceKey: 'events',
    },
    {
      icon: <Video size={48} className="text-primary-orange" />,
      title: 'Kurumsal Videolar',
      description:
        'Şirketiniz için profesyonel tanıtım videoları hazırlıyoruz.',
      features: [
        'Şirket tanıtım videoları',
        'Ürün tanıtımları',
        'Mimari projeler',
        'İnşaat süreçleri',
        'Reklam çekimleri',
      ],
      price: '₺5,000',
      duration: '3-5 gün',
      serviceKey: 'corporate',
    },
    {
      icon: <Image size={48} className="text-primary-orange" />,
      title: 'Fotoğraf Düzenleme',
      description:
        'Çekilen fotoğrafları profesyonel yazılımlarla düzenliyoruz.',
      features: [
        'Renk düzeltme',
        'Işık optimizasyonu',
        'Kompozisyon düzenleme',
        'Özel efektler',
        'Yüksek kalite çıktı',
      ],
      price: '₺500',
      duration: '1 gün',
      serviceKey: 'editing',
    },
    {
      icon: <MapPin size={48} className="text-primary-orange" />,
      title: 'Özel Projeler',
      description:
        'Özel ihtiyaçlarınıza göre özelleştirilmiş çekimler yapıyoruz.',
      features: [
        'Özel açı çekimleri',
        'Zorlu koşullarda çekim',
        'Gece çekimleri',
        'Hava durumu analizi',
        'Özel ekipman kullanımı',
      ],
      price: 'Teklif',
      duration: 'Değişken',
      serviceKey: 'custom',
    },
  ];

  useEffect(() => {
    document.title = 'Hizmetler - Yüksekkare';
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-navy to-blue-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Hizmetlerimiz
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Profesyonel drone teknolojisi ile sunduğumuz kapsamlı hizmetler. Her
            proje için özel çözümler üretiyoruz.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="card group"
              >
                <div className="mb-6 flex justify-center">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    {service.icon}
                  </motion.div>
                </div>

                <h3 className="text-2xl font-bold text-primary-navy mb-4">
                  {service.title}
                </h3>

                <p className="text-gray-600 mb-6">{service.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold text-primary-navy mb-3">
                    Özellikler:
                  </h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center space-x-2 text-sm text-gray-600"
                      >
                        <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Clock size={16} />
                      <span className="text-sm">{service.duration}</span>
                    </div>
                    <div className="text-2xl font-bold text-primary-orange">
                      {service.price}
                    </div>
                  </div>

                  <button
                    className="w-full btn-primary"
                    onClick={() =>
                      navigate(`/contact?service=${service.serviceKey}`)
                    }
                  >
                    Teklif Al
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary-navy mb-4">
              Neden Yüksekkare?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Profesyonel ekipman ve deneyimli ekibimizle kaliteli hizmet
              sunuyoruz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-primary-orange/10 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Camera size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Profesyonel Ekipman
              </h3>
              <p className="text-gray-600">
                En son teknoloji DJI drone'ları ile yüksek kaliteli çekimler
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-primary-orange/10 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Users size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Deneyimli Ekip
              </h3>
              <p className="text-gray-600">
                Yılların deneyimi ile profesyonel drone pilotları
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="bg-primary-orange/10 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Clock size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Hızlı Teslimat
              </h3>
              <p className="text-gray-600">
                Projelerinizi zamanında ve kaliteli şekilde teslim ediyoruz
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
